import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    getReleaseTypeForNonBlockingSteps,
    getRequiredIntegrationsInErrorStateForReleaseStep,
    isNonBlockingStepForReleaseType,
} from '../helpers';
import { RootState } from '../reducers/reducers';
import {
    IntegrationCapability,
    IntegrationType,
    ReleaseStatus,
    ReleaseStepStatus,
    ReleaseStepType,
    SubscriptionFeatureType,
} from '../types';
import {
    FeatureSection,
    Nullable,
    ReleaseStepComponentProps,
    ReleaseStepTabRouteComponentMatchParams,
    StepRunComponentProps,
} from '../typescriptHelpers';
import ErrorBoundary from './ErrorBoundary';
import { StepContentAppReview } from './StepContentAppReview/StepContentAppReview';
import StepContentApprovals from './StepContentApprovals';
import { StepContentBetaTesting } from './StepContentBetaTesting';
import StepContentFeatureReadiness from './StepContentFeatureReadiness';
import StepContentKickoff from './StepContentKickoff';
import { StepContentMetadata } from './StepContentMetadata';
import { StepContentRegressionTesting } from './StepContentRegressionTesting';
import { StepContentScreenshots } from './StepContentScreenshots';
import { StepContentReleaseCandidate } from './StepContentReleaseCandidate';
import { StepContentSubmission } from './StepContentSubmission/StepContentSubmission';
import { StepContentTakeoff } from './StepContentTakeoff/StepContentTakeoff';
import StepStatusHeaderComponent from './StepStatusHeaderComponent';
import { useTrackPageView } from '../helpers/trackingHelpers';
import { antGrey, runwayColors, titleStyle } from './styles/globalStyles';
import { ReleaseStepInactiveContext } from '../contexts';
import { displayTextForReleaseStepType } from '../helpers/displayHelpers';
import Text from 'antd/lib/typography/Text';
import Layout from 'antd/lib/layout/layout';
import StepContentVCSDistribution from './StepContentVCSDistribution';
import StepContentCIDistribution from './StepContentCIDistribution';
import Tabs from 'antd/lib/tabs';
import {
    ArrowRightOutlined,
    CheckCircleFilled,
    ArrowLeftOutlined,
    CloseCircleFilled,
    DashboardOutlined,
    ExclamationCircleFilled,
    ExpandOutlined,
    TeamOutlined,
    ThunderboltFilled,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import Button from 'antd/lib/button';
import {
    createAppSettingsRoute,
    createReleaseStepTabRoute,
    ReleaseStepTabType,
    ROUTER_REDIRECT,
} from '../helpers/routeHelpers';
import AutomationTab from './automations/AutomationTab';
import BetaTesters from './BetaTesters';
import {
    selectAppIntegrations,
    selectAppSettings,
    selectAppStoreType,
    selectChecklistItemsForStepRunCount,
    selectCompletedChecklistItemsForStepRunCount,
    selectHasRequiredIntegrationsWithCapabilitiesInstalledForReleaseStep,
    selectIsAppsDemoModeEnabled,
    selectIsDemoModeEnabledForRelease,
    selectIsReleaseFromScraper,
    selectRelease,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectStepRun,
    selectStepRunHasBlockedAutomation,
    selectStepRunHasFailedAutomation,
    selectStepRunOrReleaseStep,
    selectIsWorkflowsCIStepEnabled,
} from '../selectors/selectors';
import ReleaseDemoView from './releaseSteps/ReleaseDemoView';
import ReleaseStepConnectIntegrationsBanner from './releaseSteps/ReleaseStepConnectIntegrationsBanner';
import { trackReleaseStepViewEvent } from '../actions/trackEvent';
import ReleaseStepChecklistContainer from './releaseSteps/ReleaseStepChecklistContainer';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import { RestrictedStepBanner } from './planRestrictions/RestrictedStepBanner';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import { EmptyStepBanner } from './releaseEmptyState/EmptyStateBanner';
import Alert from 'antd/lib/alert';
import useSetLastViewedReleaseStep from './hooks/useSetLastViewedReleaseStep';
import StepContentRollbackBuild from './StepContentRollbackBuild';
import { ReleaseStepIntegrationStatusBanner } from './releaseSteps/ReleaseStepIntegrationStatusBanner';
import StepContentTranslations from '@/components/StepContentTranslations';
import ReleaseStepContainer from '@/components/ReleaseStepContainer';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import { Destination } from '@/types/destinations';
import { DestinationCounter } from './appHome/DestinationCounter';
import { StepRun } from '@/types/stepRun';
import { StepContentRollout } from './StepContentRollout/StepContentRollout';
import { setEventsTimelineDrawerVisibility } from '../actions/ui';

export const ReleaseStepOrStepRunContainer: React.FC<ReleaseStepOrStepRunComponentProps> = (props) => {
    const { stepRunComponentProps } = props;

    if (stepRunComponentProps) {
        return (
            <StepRunContainer
                {...props}
                stepRunComponentProps={stepRunComponentProps}
                releaseId={stepRunComponentProps.releaseId}
                appId={stepRunComponentProps.appId}
                orgId={stepRunComponentProps.orgId}
            />
        );
    } else {
        return <ReleaseStepContainer {...props} />;
    }
};

interface StepRunContainerProps {
    stepRunComponentProps: StepRunComponentProps;
    releaseId: string;
    appId: string;
    orgId: string;
}

export const StepRunContainer: React.FC<StepRunContainerProps> = (props) => {
    const { stepRunId } = props.stepRunComponentProps;

    const hasFailedEnabledAutomation = useSelector((state: RootState) =>
        selectStepRunHasFailedAutomation(state, props.appId, stepRunId),
    );

    const hasBlockedEnabledAutomation = useSelector((state: RootState) =>
        selectStepRunHasBlockedAutomation(state, props.appId, stepRunId),
    );

    const checklistItemsCount = useSelector((state: RootState) =>
        selectChecklistItemsForStepRunCount(state, stepRunId),
    );

    const completedChecklistItemsCount = useSelector((state: RootState) =>
        selectCompletedChecklistItemsForStepRunCount(state, stepRunId),
    );

    const stepStatus: ReleaseStepStatus = useSelector((state: RootState) => selectStepRun(state, stepRunId).status);
    const stepRunReasonString = useSelector((state: RootState) => selectStepRun(state, stepRunId).statusReasonString);

    const releaseStepType = useSelector((state: RootState) => selectStepRun(state, stepRunId).type);
    const title = useSelector((state: RootState) => selectStepRun(state, stepRunId).displayName);

    return (
        <ReleaseStepOrStepRunContainerBase
            {...props}
            title={title}
            releaseStepType={releaseStepType}
            checklistItemsCount={checklistItemsCount}
            completedChecklistItemsCount={completedChecklistItemsCount}
            stepRunComponentProps={props.stepRunComponentProps}
            hasFailedEnabledAutomation={hasFailedEnabledAutomation}
            hasBlockedEnabledAutomation={hasBlockedEnabledAutomation}
            stepStatus={stepStatus}
            stepStatusReasonString={stepRunReasonString}
        />
    );
};

interface ReleaseStepOrStepRunContainerBaseProps extends ReleaseStepComponentProps {
    releaseStepType: ReleaseStepType;
    checklistItemsCount: number;
    completedChecklistItemsCount: number;
    hasFailedEnabledAutomation: boolean;
    hasBlockedEnabledAutomation: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    title?: string;
    stepStatus: ReleaseStepStatus;
    stepStatusReasonString: string;
}

export const ReleaseStepOrStepRunContainerBase: React.FC<ReleaseStepOrStepRunContainerBaseProps> = (props) => {
    const {
        orgId,
        appId,
        releaseId,
        releaseStepType,
        checklistItemsCount,
        completedChecklistItemsCount,
        hasFailedEnabledAutomation,
        hasBlockedEnabledAutomation,
        stepRunComponentProps,
        title,
        stepStatus,
        stepStatusReasonString,
    } = props;

    const dispatch = useDispatch();

    const releaseStepOrStepRun = useSelector((state: RootState) => {
        return selectStepRunOrReleaseStep(state, releaseId, releaseStepType, stepRunComponentProps?.stepRunId);
    });

    const automationRuns = releaseStepOrStepRun.automationRuns;

    const stepChecklistItems = releaseStepOrStepRun.checklistItems;

    const { releaseStatus, timelinePhase } = useSelector((state: RootState) => {
        const release = selectRelease(state, releaseId);

        return {
            releaseStatus: release.status,
            timelinePhase: release.timelinePhase,
        };
    }, shallowEqual);

    const hasRequiredCapabilities = useSelector((state: RootState) =>
        selectHasRequiredIntegrationsWithCapabilitiesInstalledForReleaseStep(state, appId, releaseStepType),
    );

    const isInactiveStep =
        stepStatus === ReleaseStepStatus.Inactive ||
        releaseStatus === ReleaseStatus.Completed ||
        releaseStatus === ReleaseStatus.Skipped;

    const history = useHistory();
    const params = useParams<ReleaseStepTabRouteComponentMatchParams>();
    const activeTab = params.releaseStepTabType ?? ReleaseStepTabType.Summary;
    const shouldShowStepStatusHeader = !!stepStatus;

    const showConnectIntegrationsBanner = !hasRequiredCapabilities;

    const automationsTabAlert =
        hasFailedEnabledAutomation ?
            <CloseCircleFilled
                style={{
                    color: runwayColors.statusRed,
                    fontSize: 16,
                    marginLeft: 8,
                    marginRight: 0,
                }}
            />
        : hasBlockedEnabledAutomation ?
            <ExclamationCircleFilled
                style={{
                    color: runwayColors.statusRed,
                    fontSize: 16,
                    marginLeft: 8,
                    marginRight: 0,
                }}
            />
        :   null;

    const releaseType = useSelector((state: RootState) => {
        const release = selectRelease(state, releaseId);

        return release.type;
    });

    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );

    const showNonBlockingStepBanner = isNonBlockingStepForReleaseType(
        nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(releaseType)],
        releaseStepType,
    );

    const appIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, props.appId));
    const requiredIntegrationsInErrorState = getRequiredIntegrationsInErrorStateForReleaseStep(
        releaseStepType,
        appIntegrations,
    );

    const hasTestFlightIntegration = useSelector((state: RootState) => {
        const testFlightIntegration = selectAppIntegrations(state, appId).find(
            (integration) => integration.id === IntegrationType.TestFlight,
        );

        return !!testFlightIntegration;
    });

    const showReleaseDemoMode = useSelector((state: RootState) => {
        const releaseDemoModeEnabled = selectIsDemoModeEnabledForRelease(state, props.releaseId);
        const appsDemoModeEnabled = selectIsAppsDemoModeEnabled(state);
        // only show release demo mode banner if apps demo mode is not enabled
        return releaseDemoModeEnabled && !appsDemoModeEnabled;
    });

    const appSettingsRoute = createAppSettingsRoute(orgId, appId);

    const isReleaseFromScraper = useSelector((state: RootState) => selectIsReleaseFromScraper(state, releaseId));

    const platform = useSelector((state: RootState) => selectAppSettings(state, appId).platform);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const isCompletedRelease = releaseStatus === ReleaseStatus.Completed;

    const completedItems = `${completedChecklistItemsCount} of ${checklistItemsCount}`;
    const hasChecklistItems = checklistItemsCount > 0;
    const allCompleted = completedChecklistItemsCount === checklistItemsCount && checklistItemsCount != 0;

    const baseContainerStyles: React.CSSProperties = {
        minWidth: 0,
        flexGrow: 1,
    };

    const tabsPaneStyle: React.CSSProperties = { paddingLeft: 34, paddingRight: 34, height: '100%' };

    useSetLastViewedReleaseStep(releaseId, stepRunComponentProps?.stepRunId ?? releaseStepType, [
        releaseStepType,
        stepRunComponentProps?.stepRunId,
    ]);

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));

    const releaseStepTypeText = displayTextForReleaseStepType(
        releaseStepType,
        platform,
        appStoreType,
        isWorkflowsCIStepEnabled,
    );
    const finalTitle = title || releaseStepTypeText;

    useTrackPageView(finalTitle, [releaseStepType]);

    React.useEffect(() => {
        dispatch(
            trackReleaseStepViewEvent({
                orgId,
                appId,
                releaseId,
                releaseStepType,
            }),
        );
    }, [props.orgId, props.appId, props.releaseId, props.releaseStepType]);

    return (
        <>
            <Layout style={{ minWidth: 1000, position: 'relative' }}>
                <StepTopBar
                    releaseStepTypeText={releaseStepTypeText}
                    stepName={title}
                    releaseStepType={releaseStepType}
                    destinations={(releaseStepOrStepRun as StepRun)?.destinations}
                />
                <ReleaseStepIntegrationStatusBanner
                    orgId={orgId}
                    appId={appId}
                    integrations={requiredIntegrationsInErrorState}
                />
                <EmptyStateWrapper shouldForceDisable releaseId={releaseId} featureSections={[]} appId={appId}>
                    {(isInEmptyState) => {
                        return (
                            isInEmptyState && (
                                <EmptyStepBanner
                                    releaseStepType={releaseStepType}
                                    appId={appId}
                                    releaseId={releaseId}
                                    isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                />
                            )
                        );
                    }}
                </EmptyStateWrapper>
                <PlanRestrictedFeatureWrapper
                    feature={
                        releaseStepType === ReleaseStepType.Approvals ? SubscriptionFeatureType.ApprovalsStep : null
                    }
                    integrationCapability={
                        releaseStepType === ReleaseStepType.Translations ? IntegrationCapability.Translations : null
                    }
                >
                    {(isDisabled) => (
                        <>
                            {isDisabled ?
                                <RestrictedStepBanner />
                            :   showNonBlockingStepBanner && (
                                    <div
                                        style={{
                                            backgroundColor: antGrey.grey3,
                                            padding: '24px 36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Text style={{ fontWeight: 600, color: antGrey.grey8 }}>
                                            This step is not required.
                                        </Text>
                                        <Button
                                            type="link"
                                            style={{ paddingRight: 0 }}
                                            onClick={() => {
                                                history.push(appSettingsRoute);
                                            }}
                                        >
                                            Release step settings
                                            <ArrowRightOutlined style={{ color: runwayColors.info }} />
                                        </Button>
                                    </div>
                                )
                            }
                        </>
                    )}
                </PlanRestrictedFeatureWrapper>
                <Layout
                    style={{
                        ...baseContainerStyles,
                        overflowY: 'scroll',
                    }}
                >
                    <div
                        style={{
                            flexShrink: 0,
                            marginBottom: 24,
                            marginTop: 24,
                            display: 'flex',
                            flexDirection: 'column',
                            paddingLeft: 34,
                            paddingRight: 34,
                        }}
                    >
                        {(showConnectIntegrationsBanner || showReleaseDemoMode) && !isCompletedRelease && (
                            <ReleaseDemoView releaseId={releaseId} orgId={orgId} appId={appId} />
                        )}
                        <EmptyStateWrapper shouldForceDisable releaseId={releaseId} featureSections={[]} appId={appId}>
                            {(isInEmptyState) => {
                                return isInEmptyState ?
                                        <Alert
                                            icon={<ExpandOutlined />}
                                            message="This step was completed outside of Runway."
                                            type="info"
                                            style={{
                                                backgroundColor: antGrey.grey2,
                                                borderColor: antGrey.grey5,
                                                width: 354,
                                            }}
                                            showIcon
                                        />
                                    :   showConnectIntegrationsBanner && (
                                            <ReleaseStepConnectIntegrationsBanner {...props} />
                                        );
                            }}
                        </EmptyStateWrapper>
                        {!isReleaseFromScraper && (
                            <PlanRestrictedFeatureWrapper
                                feature={
                                    releaseStepType === ReleaseStepType.Approvals ?
                                        SubscriptionFeatureType.ApprovalsStep
                                    :   null
                                }
                                integrationCapability={
                                    releaseStepType === ReleaseStepType.Translations ?
                                        IntegrationCapability.Translations
                                    :   null
                                }
                            >
                                {(isDisabled) =>
                                    shouldShowStepStatusHeader &&
                                    !isDisabled && (
                                        <StepStatusHeaderComponent
                                            orgId={orgId}
                                            orgIdParam={params.orgIdParam}
                                            appIdParam={params.appIdParam}
                                            releaseTimelinePhase={timelinePhase}
                                            releaseId={releaseId}
                                            releaseStepType={releaseStepType}
                                            appId={appId}
                                            status={stepStatus}
                                            statusReasonString={stepStatusReasonString}
                                        />
                                    )
                                }
                            </PlanRestrictedFeatureWrapper>
                        )}
                    </div>
                    <PlanRestrictedFeatureWrapper
                        feature={
                            releaseStepType === ReleaseStepType.Approvals ? SubscriptionFeatureType.ApprovalsStep : null
                        }
                        integrationCapability={
                            releaseStepType === ReleaseStepType.Translations ? IntegrationCapability.Translations : null
                        }
                    >
                        {(isDisabled) => (
                            <Tabs
                                type="card"
                                tabBarExtraContent={{
                                    right: activeTab === ReleaseStepTabType.Summary && (
                                        <>
                                            <UnorderedListOutlined style={{ marginRight: 5, color: antGrey.grey7 }} />
                                            <Text
                                                style={{
                                                    fontSize: '13px',
                                                    color: antGrey.grey7,
                                                    letterSpacing: '0.1em',
                                                    textTransform: 'uppercase',
                                                    fontWeight: 400,
                                                }}
                                            >
                                                Timeline
                                            </Text>
                                            <Divider type="vertical" style={{ color: antGrey.grey5 }} />
                                            <Button
                                                type="link"
                                                disabled={isDisabled}
                                                style={{ padding: 0, margin: 0, fontSize: '13px' }}
                                                onClick={() => {
                                                    dispatch(
                                                        setEventsTimelineDrawerVisibility(
                                                            stepRunComponentProps?.stepRunId ?? releaseStepType,
                                                            true,
                                                        ),
                                                    );
                                                }}
                                            >
                                                View
                                                <ArrowLeftOutlined />
                                            </Button>
                                        </>
                                    ),
                                }}
                                tabBarStyle={{
                                    marginLeft: 34,
                                    marginRight: 34,
                                    marginBottom: 0,
                                }}
                                onTabClick={(key) => {
                                    const tabType = key as ReleaseStepTabType;
                                    const route = createReleaseStepTabRoute(
                                        params.orgIdParam,
                                        params.appIdParam,
                                        params.releaseIdParam,
                                        params.releaseStepTypeParam,
                                        tabType,
                                    );

                                    history.push(route, {
                                        [ROUTER_REDIRECT]: true,
                                    });
                                }}
                                activeKey={activeTab}
                                destroyInactiveTabPane
                                className="release-step-tabs"
                            >
                                <Tabs.TabPane
                                    disabled={isDisabled}
                                    key={ReleaseStepTabType.Summary}
                                    tab={
                                        <div>
                                            <DashboardOutlined
                                                style={{
                                                    color:
                                                        activeTab === ReleaseStepTabType.Summary ?
                                                            runwayColors.info
                                                        :   antGrey.grey6,
                                                }}
                                            />
                                            <Text
                                                style={{
                                                    color:
                                                        activeTab === ReleaseStepTabType.Summary ?
                                                            antGrey.grey8
                                                        :   antGrey.grey7,
                                                }}
                                            >
                                                Summary
                                            </Text>
                                        </div>
                                    }
                                    style={tabsPaneStyle}
                                >
                                    <ReleaseStepInactiveContext.Provider value={isInactiveStep}>
                                        <ErrorBoundary showRedirectLink={false} key={releaseStepType}>
                                            {stepContent(releaseStepType, stepRunComponentProps, props)}
                                        </ErrorBoundary>
                                    </ReleaseStepInactiveContext.Provider>
                                </Tabs.TabPane>
                                {releaseStepType === ReleaseStepType.BetaTesting && hasTestFlightIntegration && (
                                    <Tabs.TabPane
                                        disabled={isDisabled}
                                        key={ReleaseStepTabType.Testers}
                                        tab={
                                            <div>
                                                <TeamOutlined
                                                    style={{
                                                        color:
                                                            activeTab === ReleaseStepTabType.Testers ?
                                                                runwayColors.info
                                                            :   antGrey.grey6,
                                                    }}
                                                />
                                                <Text
                                                    style={{
                                                        color:
                                                            activeTab === ReleaseStepTabType.Testers ?
                                                                antGrey.grey8
                                                            :   antGrey.grey7,
                                                    }}
                                                >
                                                    Testers
                                                </Text>
                                            </div>
                                        }
                                        style={tabsPaneStyle}
                                    >
                                        <ReleaseStepInactiveContext.Provider value={isInactiveStep}>
                                            <ErrorBoundary showRedirectLink={false} key={releaseStepType}>
                                                <BetaTesters
                                                    releaseId={releaseId}
                                                    releaseStepType={releaseStepType}
                                                    stepRunComponentProps={stepRunComponentProps}
                                                />
                                            </ErrorBoundary>
                                        </ReleaseStepInactiveContext.Provider>
                                    </Tabs.TabPane>
                                )}
                                <Tabs.TabPane
                                    disabled={isDisabled}
                                    className="step-tab-automations"
                                    key={ReleaseStepTabType.Automations}
                                    tab={
                                        <EmptyStateWrapper
                                            appId={appId}
                                            featureSections={[FeatureSection.AutomationTab]}
                                            shouldForceDisable={activeTab === ReleaseStepTabType.Automations}
                                            releaseId={releaseId}
                                            shouldShowAsTooltip
                                        >
                                            <div>
                                                <ThunderboltFilled
                                                    style={{
                                                        color:
                                                            activeTab === ReleaseStepTabType.Automations ?
                                                                runwayColors.info
                                                            :   antGrey.grey6,
                                                    }}
                                                />
                                                <Text
                                                    style={{
                                                        color:
                                                            activeTab === ReleaseStepTabType.Automations ?
                                                                antGrey.grey8
                                                            :   antGrey.grey7,
                                                    }}
                                                >
                                                    Automation
                                                </Text>
                                                {automationsTabAlert}
                                            </div>
                                        </EmptyStateWrapper>
                                    }
                                    style={tabsPaneStyle}
                                >
                                    <ErrorBoundary showRedirectLink={false} key={releaseStepType}>
                                        <AutomationTab
                                            {...props}
                                            isReleaseStep={true}
                                            automationRuns={automationRuns}
                                        />
                                    </ErrorBoundary>
                                </Tabs.TabPane>
                                <Tabs.TabPane
                                    disabled={isDisabled}
                                    key={ReleaseStepTabType.Checklist}
                                    tab={
                                        <EmptyStateWrapper
                                            appId={appId}
                                            featureSections={[FeatureSection.ChecklistTab]}
                                            shouldForceDisable={activeTab === ReleaseStepTabType.Checklist}
                                            releaseId={releaseId}
                                            shouldShowAsTooltip
                                        >
                                            <div>
                                                <UnorderedListOutlined
                                                    style={{
                                                        color:
                                                            activeTab === ReleaseStepTabType.Checklist ?
                                                                runwayColors.info
                                                            :   antGrey.grey6,
                                                    }}
                                                />
                                                <Text
                                                    style={{
                                                        color:
                                                            activeTab === ReleaseStepTabType.Checklist ?
                                                                antGrey.grey8
                                                            :   antGrey.grey7,
                                                    }}
                                                >
                                                    Checklist {hasChecklistItems && '/ '}
                                                </Text>
                                                {hasChecklistItems && (
                                                    <Text
                                                        style={{
                                                            color:
                                                                allCompleted ?
                                                                    runwayColors.statusGreen
                                                                :   runwayColors.statusYellow,
                                                        }}
                                                    >
                                                        {completedItems}
                                                    </Text>
                                                )}
                                                {allCompleted && (
                                                    <CheckCircleFilled
                                                        style={{ color: runwayColors.statusGreen, marginLeft: 4 }}
                                                    />
                                                )}
                                            </div>
                                        </EmptyStateWrapper>
                                    }
                                    className="step-tab-checklist"
                                    style={tabsPaneStyle}
                                >
                                    <ReleaseStepChecklistContainer {...props} checklistItems={stepChecklistItems} />
                                </Tabs.TabPane>
                            </Tabs>
                        )}
                    </PlanRestrictedFeatureWrapper>
                </Layout>
            </Layout>
        </>
    );
};

const StepTopBar: React.FC<{
    releaseStepTypeText: string;
    releaseStepType: ReleaseStepType;
    stepName?: string;
    destinations: Destination[];
}> = ({ releaseStepType, stepName, releaseStepTypeText, destinations }) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 76,
                width: '100%',
                paddingRight: 24,
                paddingLeft: 36,
                flexGrow: 0,
                flexShrink: 0,
                backgroundColor: antGrey.grey3,
            }}
        >
            <Space
                split={
                    <Divider
                        type="vertical"
                        style={{
                            backgroundColor: antGrey.grey5,
                            height: 22,
                        }}
                    />
                }
            >
                <PlanRestrictedFeatureWrapper
                    requiresTooltip
                    feature={
                        releaseStepType === ReleaseStepType.Approvals ? SubscriptionFeatureType.ApprovalsStep : null
                    }
                    integrationCapability={
                        releaseStepType === ReleaseStepType.Translations ? IntegrationCapability.Translations : null
                    }
                >
                    <Text
                        style={{
                            ...titleStyle,
                            fontSize: 18,
                        }}
                    >
                        {releaseStepTypeText}
                    </Text>
                </PlanRestrictedFeatureWrapper>
                {stepName && stepName !== releaseStepTypeText && (
                    <Text
                        style={{
                            ...titleStyle,
                            fontSize: 18,
                            fontWeight: 400,
                        }}
                    >
                        {stepName}
                    </Text>
                )}
                <Space>
                    {destinations &&
                        destinations.map((destination) => <DestinationCounter destinations={[destination]} />)}
                </Space>
            </Space>
        </div>
    );
};

const stepContent = (
    releaseStepType: string,
    stepRunComponentProps: Nullable<StepRunComponentProps>,
    props: ReleaseStepOrStepRunContainerBaseProps,
) => {
    switch (releaseStepType) {
        case ReleaseStepType.Kickoff:
            return <StepContentKickoff {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.FeatureReadiness:
            return <StepContentFeatureReadiness {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.ReleaseCandidate:
            return <StepContentReleaseCandidate {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.RegressionTesting:
            return <StepContentRegressionTesting {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.BetaTesting:
            return <StepContentBetaTesting {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.Metadata:
            return <StepContentMetadata {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.Screenshots:
            return <StepContentScreenshots {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.Approvals:
            return <StepContentApprovals {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.Submission:
            return <StepContentSubmission {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.StoreReview:
            return <StepContentAppReview {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.Takeoff:
            return <StepContentTakeoff {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.VCSDistribution:
            return <StepContentVCSDistribution {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.CIDistribution:
            return <StepContentCIDistribution {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.RollbackBuild:
            return <StepContentRollbackBuild {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.Translations:
            return <StepContentTranslations {...props} stepRunComponentProps={stepRunComponentProps} />;
        case ReleaseStepType.Rollout:
            return <StepContentRollout {...props} stepRunComponentProps={stepRunComponentProps} />;
        default:
            return null;
    }
};
